/**
 * Auth Selectors
 *
 * Centralized selectors for auth lifecycle state.
 * All components should use these instead of reading raw Redux state.
 */

import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import type { AuthLifecycle } from '../dashboard/reducers/userSlice';

/**
 * Select the current auth lifecycle state.
 * Falls back to UNAUTHENTICATED if state is corrupted or missing.
 */
export const selectAuthLifecycle = (state: RootState): AuthLifecycle =>
  state?.userReducer?.user?.authLifecycle ?? 'UNAUTHENTICATED';

/**
 * True only when the user has a confirmed, active session.
 */
export const selectIsAuthenticated = createSelector(
  selectAuthLifecycle,
  (lc) => lc === 'AUTHENTICATED',
);

/**
 * True when the app is actively verifying a persisted token.
 */
export const selectIsRestoring = createSelector(
  selectAuthLifecycle,
  (lc) => lc === 'RESTORING',
);

/**
 * True when logout cleanup is in progress.
 * Guards should show a loader (not redirect) in this state.
 */
export const selectIsLoggingOut = createSelector(
  selectAuthLifecycle,
  (lc) => lc === 'LOGGING_OUT',
);
